import {Injectable} from '@angular/core';
import {ApiConfig} from "../../api";
import {Session} from "../../../session/session";
import {AdnHttpService} from '../../../util/adn-http.service';
import {AdnHttpSender, createAdnHttpResourceSender} from '../../../util/create-adn-http-resource';
import {AuthHttpInterceptorService} from '../../../auth/auth-http-interceptor.service';
import {ApiErrorHandlerService} from '../../../../app/common/directives/error/api-error-handler.service';



function b64urlToBytes(b64u: string): Uint8Array {
  const pad = "=".repeat((4 - (b64u.length % 4)) % 4);
  const b64 = (b64u + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function bufToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = ""; for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function prepareCreationOptions(publicKey: any): PublicKeyCredentialCreationOptions {
  return {
    ...publicKey,
    challenge: b64urlToBytes(publicKey.challenge),
    user: {
      ...publicKey.user,
      id: b64urlToBytes(publicKey.user.id),
    },
    excludeCredentials: Array.isArray(publicKey.excludeCredentials)
      ? publicKey.excludeCredentials.map((c: any) => ({
        ...c,
        id: b64urlToBytes(c.id),
      }))
      : undefined,
  };
}

function prepareRequestOptions(publicKey: any): PublicKeyCredentialRequestOptions {
  return {
    ...publicKey,
    challenge: b64urlToBytes(publicKey.challenge),
    allowCredentials: Array.isArray(publicKey.allowCredentials)
      ? publicKey.allowCredentials.map((c: any) => ({
        ...c,
        id: b64urlToBytes(c.id),
      }))
      : undefined,
  };
}

// these are just so that type script stops bitching
interface AssertionResponseWithTransports extends AuthenticatorAssertionResponse {
  getTransports?: () => string[];
}

@Injectable()
export class WebAuthnResource {
  private readonly url = ApiConfig.obtain().getVersionedUri('webauthn');
  private readonly send: AdnHttpSender;

  constructor(
    AdnHttp: AdnHttpService,
    authHttpInterceptor: AuthHttpInterceptorService,
    ApiErrorHandler: ApiErrorHandlerService
  ) {
    this.send = createAdnHttpResourceSender(AdnHttp, authHttpInterceptor, ApiErrorHandler);
  }

  async loginWithPasskey(credentials: { username: any, masqUsername: any }) {
   const optionsRes: any = await this.send({
      method: 'POST',
      url: this.url + "/authentication/options",
      body: {username: credentials.username}
    })
      .catch((err: any) => {
        if (err.data) {
          throw err.data;
        }
      });
    const publicKeyRes = optionsRes.publicKey;
    const requestId = optionsRes.requestId;

    // so if we are trying to sign in a user with a specified username and there are no allow credentials it does
    // mean there are no back end credentials for this user and we can short circuit the next process.
/*
    if (credentials.username && (!publicKeyRes.allowCredentials || publicKeyRes.allowCredentials.length === 0)) {
       throw {text: "No Passkeys are registered to your user. You can set up a Passkey on your User Profile once you have signed in."};
    }
*/

    const publicKey = prepareRequestOptions(publicKeyRes);
    const credAny = await navigator.credentials.get({ publicKey })
      .catch((err: any) => {
        throw err;
      });
    if (!credAny) {
      throw {uiResponse: 1, text: "No relevant Passkeys were found on your device."};
    }

    const cred = credAny as PublicKeyCredential;
    const resp = cred.response as AssertionResponseWithTransports;
    const transports = resp.getTransports ? resp.getTransports() : [];

    const payload = {
      requestId,
      masqueradeAsUser: credentials.masqUsername,
      credential: {
        id: cred.id,
        rawId: bufToB64url(cred.rawId),
        type: cred.type,
        response: {
          clientDataJSON: bufToB64url(resp.clientDataJSON),
          authenticatorData: bufToB64url(resp.authenticatorData),
          signature: bufToB64url(resp.signature),
          userHandle: resp.userHandle ? bufToB64url(resp.userHandle) : null
        },
        transports: transports
      }
    };

    const data: any = await this.send({
      method: 'POST',
      url: `${this.url}/authentication/result`,
      params: {requestId},
      body: payload
    }).catch((err: any) => {
      if (err.data) {
        throw err.data;
      }
    });

    return Session.obtain().init(
      data.scope,
      data.access_token,
      data.refresh_token,
      data.username,
      data.expires_in,
      data.username
    );
  }

  existingCredentials() {
    const promise = this.send({
      method: 'GET',
      url: this.url + "/credentials",
      params: {includeDeleted: true}
    })
      .catch(() => null);
    return { $promise: promise };
  }

  deleteCredential(id: string) {
    return this.send({method: 'POST', url: this.url + "/credential/delete/" + id, body: {}})
      .catch(() => null);
  }

  updateDescription(id: string, description: string) {
    return this.send({
      method: 'POST',
      url: this.url + "/credential/description/" + id,
      body: {description: description}
    })
      .catch(() => null);
  }

  restoreCredential(id: string) {
    return this.send({method: 'POST', url: this.url + "/credential/restore/" + id, body: {}})
      .catch(() => null);
  }

  async registerCredentialsOptions() {
    const response = await this.send({method: 'POST', url: this.url + "/registration/options", body: {}})
      .catch((err: any) => {
        throw err;
      });

    const requestId = response.requestId;
    const publicKey = prepareCreationOptions(response.publicKey);
    const cred = await navigator.credentials.create({publicKey}).catch((err) => {
      if (err.name === 'InvalidStateError') {
        throw {text: 'Passkey is already registered'};
      } else {
        return null;
      }
    });

    if (!cred) {
      return null;
    }
    const pkc = cred as PublicKeyCredential;
    const att = pkc.response as AuthenticatorAttestationResponse;

    const transports = att.getTransports ? att.getTransports() : [];
    return {
      requestId: requestId,
      credential: {
        id: cred.id,
        rawId: bufToB64url(pkc.rawId),
        type: cred.type,
        response: {
          clientDataJSON: bufToB64url(att.clientDataJSON),
          attestationObject: bufToB64url(att.attestationObject)
        },
        transports: transports
      }
    };
  }

  registerCredentials(twoFactorAuthCode: string, description: string, payload: any) {
    payload.twoFactorAuthCode = twoFactorAuthCode;
    payload.description = description;

    return this.send({
      method: 'POST',
      url: `${this.url}/registration/result`,
      body: payload
    })
      .then(() => true)
      .catch((err: any) => {
        throw err;
      });
  }
}
